import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  AlertCircle,
  Banknote,
  CheckCircle2,
  FileText,
  Info,
  Search,
  Undo2,
  X,
} from "lucide-react";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";
import Pagination from "../../../components/Pagination";

import {
  listPendingRefunds,
  issueOrReleaseRefund,
  coerceRefundStatus,
  coerceRefundReason,
  formatMoney,
  type BillingRefundQueueStatus,
  type PlatformPendingRefundRow,
} from "../../../api/General/PlatformBilling";
import { formatDate, daysFromNow } from "./helpers";
import { fieldClass, ErrorNote, DialogIcon, firstError } from "./PlatformBillingDialogs";

/*
 * REFUND RELEASE QUEUE (CO-43) — the screen an over-cap refund was missing.
 *
 * 🔴 WHY THIS PAGE EXISTS. A refund above `Billing:AutomaticRefundCap` is written `PendingApproval`
 * and then waits for a person. The release endpoint has always existed and takes a `refundId` — but
 * nothing in the product could tell an Operator what those ids were, so the refund could never be
 * released. Money the customer was owed sat parked, indefinitely, invisibly.
 *
 * 🔴 BR-36 / §7.6 — THERE IS NO DENIAL, AND THIS PAGE MUST NEVER GROW ONE. `BillingRefundStatus` has
 * no `Rejected` value, by construction. A parked refund has exactly two futures: released, or still
 * parked. That is not an omission to be "completed" later — the refusal to model denial is the policy,
 * and it is stated on screen (`NoDenialNote`) rather than left as folklore, because the first question
 * an operator asks a queue with one button is "where is the other button?".
 *
 * Design calls, so a later pass does not undo the deliberate parts:
 *
 * • It borrows the Subscriptions console's exact chrome (header band, table primitives, chip shape,
 *   tabular-nums money). Same job, second view. Consistency is the affordance.
 * • Oldest first, and "waiting since" is rendered as an AGE, not a date. This is an SLA queue: the
 *   number that matters is how long a customer has been owed money, and a date makes the reader do
 *   that arithmetic themselves.
 * • The rule path is shown ON THE ROW, not behind a drawer. It is the answer to "what am I about to
 *   approve?", and hiding it guarantees the release becomes a reflex click.
 * • No KPI strip, no stat cards. A queue with a big "3" over it teaches an operator to read the number
 *   instead of the rows.
 * • Nothing releases on a single click, and the confirm names the OUTCOME and the AMOUNT.
 *
 * ⚠️ Role Model: Operator is a platform actor. Nothing here reads employee data — a company name,
 * money, a rule path and two ids.
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const chipBase =
  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider";

/** The two states a person can act on. There is no third — see BR-36 above. */
const STATUS_FILTERS: { value: "" | BillingRefundQueueStatus; key: string }[] = [
  { value: "", key: "Everything waiting" },
  { value: "PendingApproval", key: "Waiting on release" },
  { value: "Failed", key: "Settlement failed" },
];

/** Plain-language names for `BillingRefundReason`. The enum name is not operator vocabulary. */
const REASON_LABELS: Record<string, string> = {
  MoneyBackWindow: "Money-back window",
  ProratedCancellation: "Cancelled part-way through the term",
  NoticeDefective: "Price-change notice was defective",
  OperatorGoodwill: "Goodwill",
  Chargeback: "Chargeback",
  Correction: "Correction",
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * The BR-36 disclosure, stated once and stated plainly.
 *
 * A full border and a muted tint, never a side stripe (banned). It is one sentence because an
 * operator reads a policy note exactly once, and only if it is short.
 */
function NoDenialNote() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-3.5 py-2.5">
      <Info size={15} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="text-xs leading-relaxed text-muted-foreground max-w-[80ch]">
        {/*
          🔴 `nsSeparator: false` is LOAD-BEARING, not tidying. This key contains a colon
          ("...no reject action: a refund left..."), and i18next's default nsSeparator is ':',
          so without this option it splits the string, treats everything before the colon as a
          NAMESPACE, fails that lookup, and renders ONLY the fragment after the colon.

          The result was device-proven on dev 2026-08-10: the whole BR-36 statement — the words
          "Mowazaf has no way to refuse a refund, so there is no reject action" — silently vanished
          from a money surface, leaving a sentence that reads like a note about ageing amounts. The
          translations were complete and correct in en/ar/fr the whole time; nothing was missing but
          this option. i18n.js already sets `keySeparator: false` for exactly this reason with '.',
          which is why natural-language keys work at all here; ':' was simply missed.

          ⚠️ Do NOT "fix" this globally by setting nsSeparator: false in i18n.js — 651 call sites
          legitimately use the `namespace:key` form (attendance:, payroll:, leaves:, …).
          52 other sites have the same silent truncation; tracked as CO-62.
        */}
        {t(
          "Releasing is the only decision here. Mowazaf has no way to refuse a refund, so there is no reject action: a refund left in this queue is still owed, and still growing older. The amounts were computed at purchase and cannot be edited by anyone.",
          { nsSeparator: false },
        )}
      </p>
    </div>
  );
}

/* ═══════════════════════ Release confirmation ═══════════════════════ */

function ReleaseRefundDialog({
  open,
  onOpenChange,
  row,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: PlatformPendingRefundRow | null;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const reasonInvalid = reason.trim().length === 0;
  const amountLabel = formatMoney(row.amountGross, row.currency);
  const status = coerceRefundStatus(row.status);
  const isRetry = status === "Failed";

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      /*
       * ⚠️ NO AMOUNT IS SENT. The release shape of this endpoint carries a reason and nothing else —
       * the server reads the figure off the refund row. `currency` and `amountNet` sit on the shared
       * input type only because the manual bank-transfer shape needs them; on this path the server
       * ignores them, and they must never be sourced from anything the operator typed.
       */
      const result = await issueOrReleaseRefund(
        row.companyId,
        { reason: 0, currency: row.currency, amountNet: 0, reasonNote: reason.trim() },
        row.id,
      );
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not release the refund. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="danger">
              <Undo2 size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>
                {isRetry ? t("Send this refund again") : t("Release this refund")}
              </DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {isRetry
              ? t(
                  "Settlement gave up on this refund after {{attempts}} attempts. Releasing it again puts it back in front of the payment provider from a clean slate. The amount is unchanged.",
                  { attempts: row.settlementAttempts },
                )
              : t(
                  "This hands {{amount}} back to the customer through the payment provider. It cannot be undone from here, and the amount cannot be changed by anyone.",
                  { amount: amountLabel },
                )}
          </p>

          {/* The figure, broken out. An operator approving money should see what the tax component is. */}
          <dl className="rounded-lg border border-border divide-y divide-border text-sm">
            <div className="flex items-center justify-between px-3.5 py-2">
              <dt className="text-muted-foreground">{t("Before tax")}</dt>
              <dd className="tabular-nums">{formatMoney(row.amountNet, row.currency)}</dd>
            </div>
            <div className="flex items-center justify-between px-3.5 py-2">
              <dt className="text-muted-foreground">{t("Tax")}</dt>
              <dd className="tabular-nums">{formatMoney(row.taxAmount, row.currency)}</dd>
            </div>
            <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/40">
              <dt className="font-medium">{t("Returned to the customer")}</dt>
              <dd className="tabular-nums font-semibold">{amountLabel}</dd>
            </div>
          </dl>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("Why this was parked")}
            </p>
            <p className="text-sm font-mono text-foreground break-words">{row.rulePath}</p>
          </div>

          {/*
            BR-11: settlement is blocked until a credit note exists. Saying so BEFORE the click is the
            difference between "released and waiting on finance" and an operator who believes the money
            moved and closes the ticket.
          */}
          {!row.creditNoteId && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {t(
                  "No credit note has been issued for this refund yet, so releasing it will not move money on its own. It stays owed and visible until finance settles it by hand.",
                )}
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="rq-release-reason">
              {t("Reason")} <span className="text-destructive">*</span>
            </label>
            <textarea
              id="rq-release-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={t("e.g. reviewed against the March invoice, figure agreed with finance")}
              className={fieldClass(touched && reasonInvalid)}
            />
            {touched && reasonInvalid ? (
              <p className="text-xs text-destructive">
                {t("A reason is required for every override.")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("Recorded against this company and shown to the customer.")}
              </p>
            )}
          </div>

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          {/*
            Destructive + names the OUTCOME, not the dialog title: this moves real money out and cannot
            be reversed from this screen.
          */}
          <Button variant="destructive" onClick={submit} loading={submitting}>
            {t("Return {{amount}} to the customer", { amount: amountLabel })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ The queue ═══════════════════════ */

export default function RefundQueueIndex() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  const [rows, setRows] = useState<PlatformPendingRefundRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [status, setStatus] = useState<"" | BillingRefundQueueStatus>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [releaseRow, setReleaseRow] = useState<PlatformPendingRefundRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await listPendingRefunds({
        search: debouncedSearch || undefined,
        status: status || undefined,
        page,
        pageSize,
      });
      if (result?.errors?.length) {
        setErrorMsg(result.errors[0]?.description || t("Could not load the refund queue."));
        setRows([]);
        setTotalCount(0);
        return;
      }
      setRows(result.payload?.items ?? []);
      setTotalCount(result.payload?.totalCount ?? 0);
    } catch {
      setErrorMsg(t("Could not load the refund queue."));
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, page, pageSize, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Any filter change returns to the first page; staying on page 4 of a now-2-page result is a blank screen.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, pageSize]);

  const totalPages = useMemo(
    () => (pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1),
    [totalCount, pageSize],
  );

  /** Age, not a date. See the header comment: this is an SLA queue. */
  const waitingLabel = (iso: string): { label: string; overdue: boolean } => {
    const delta = daysFromNow(iso);
    const days = delta === null ? 0 : Math.abs(Math.min(delta, 0));
    if (delta === null) return { label: "—", overdue: false };
    if (days === 0) return { label: t("today"), overdue: false };
    return {
      label: t("{{days}}d", { days }),
      // §7.6 sets a 5-business-day SLA. Past a week, the row is late by any reading of it.
      overdue: days > 7,
    };
  };

  return (
    <div className="min-h-full bg-muted/20">
      <Toaster position={isRtl ? "top-left" : "top-right"} />

      {/* Header band — same shape as the Subscriptions console, deliberately. */}
      <div className="border-b border-border bg-card">
        <div className="max-w-[1600px] mx-auto px-6 py-5 flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 dark:from-slate-200 dark:to-slate-400 text-white dark:text-slate-900 flex items-center justify-center shadow-sm">
            <Banknote size={22} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-foreground tracking-tight">
                {t("Refund Release Queue")}
              </h1>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium tabular-nums bg-muted text-muted-foreground border border-border">
                {totalCount}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("Refunds above the automatic cap wait here until a person releases them.")}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-4">
        <NoDenialNote />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search
              size={15}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search by company")}
              aria-label={t("Search by company")}
              className="w-full h-9 rounded-md border border-input bg-background ps-9 pe-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label={t("Clear search")}
                className="absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value || "all"}
                type="button"
                aria-pressed={status === option.value}
                onClick={() => setStatus(option.value)}
                className={
                  status === option.value
                    ? "px-2.5 h-8 rounded text-xs font-medium bg-muted text-foreground"
                    : "px-2.5 h-8 rounded text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                }
              >
                {t(option.key)}
              </button>
            ))}
          </div>
        </div>

        {errorMsg && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="ps-4">{t("Company")}</TableHead>
                <TableHead className="text-end">{t("Waiting")}</TableHead>
                <TableHead className="text-end">{t("Owed to customer")}</TableHead>
                <TableHead>{t("Why it is here")}</TableHead>
                <TableHead>{t("Invoice")}</TableHead>
                <TableHead className="text-end pe-4">{t("Action")}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <div className="flex justify-center">
                      <Spinner />
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    {/*
                      An empty release queue is GOOD NEWS, and it says so. The second sentence is the
                      part that matters: it tells an operator who expected to find something here why
                      it is not here, instead of leaving them to wonder whether the page is broken.
                    */}
                    <div className="flex flex-col items-center gap-2 text-center">
                      <CheckCircle2 size={22} className="text-muted-foreground" aria-hidden="true" />
                      <p className="text-sm font-medium text-foreground">
                        {search || status
                          ? t("No refunds match these filters.")
                          : t("Nothing is waiting on a person.")}
                      </p>
                      <p className="text-xs text-muted-foreground max-w-[52ch]">
                        {t(
                          "Refunds at or below the automatic cap settle on their own and never appear here.",
                        )}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const waiting = waitingLabel(row.requestedAtUtc);
                  const rowStatus = coerceRefundStatus(row.status);
                  const failed = rowStatus === "Failed";
                  const reasonKey = coerceRefundReason(row.reason);
                  const reasonLabel = reasonKey ? REASON_LABELS[reasonKey] ?? reasonKey : null;

                  return (
                    <TableRow key={row.id}>
                      <TableCell className="ps-4 align-top">
                        <div className="font-medium text-foreground">{row.companyName}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                          {t("Refund #{{id}}", { id: row.id })}
                        </div>
                        {failed && (
                          <span className={`${chipBase} mt-1.5 bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300`}>
                            {t("Settlement failed")}
                          </span>
                        )}
                      </TableCell>

                      <TableCell className="align-top text-end tabular-nums">
                        <span
                          className={
                            waiting.overdue
                              ? "font-medium text-amber-700 dark:text-amber-400"
                              : "text-muted-foreground"
                          }
                        >
                          {waiting.label}
                        </span>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {formatDate(row.requestedAtUtc)}
                        </div>
                      </TableCell>

                      <TableCell className="align-top text-end tabular-nums">
                        <div className="font-medium text-foreground">
                          {formatMoney(row.amountGross, row.currency)}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {t("incl. {{tax}} tax", {
                            tax: formatMoney(row.taxAmount, row.currency),
                          })}
                        </div>
                      </TableCell>

                      <TableCell className="align-top max-w-[26rem]">
                        {reasonLabel && (
                          <div className="text-sm text-foreground">{t(reasonLabel)}</div>
                        )}
                        <div className="mt-0.5 text-xs font-mono text-muted-foreground break-words">
                          {row.rulePath}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {t("{{consumed}} of {{term}} months used", {
                            consumed: row.consumedMonths,
                            term: row.termMonths,
                          })}
                        </div>
                        {failed && row.lastSettlementError && (
                          <div className="mt-1 text-xs text-red-700 dark:text-red-400 break-words">
                            {row.lastSettlementError}
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="align-top text-sm text-muted-foreground">
                        {row.invoiceNumber || row.billingInvoiceId ? (
                          <span className="inline-flex items-center gap-1.5">
                            <FileText size={13} aria-hidden="true" />
                            <span className="tabular-nums">
                              {row.invoiceNumber ?? `#${row.billingInvoiceId}`}
                            </span>
                          </span>
                        ) : (
                          t("No invoice")
                        )}
                        {/* BR-11 again, at a glance: a released refund with no credit note is still manual. */}
                        {!row.creditNoteId && (
                          <div className="mt-1 text-xs">{t("No credit note yet")}</div>
                        )}
                      </TableCell>

                      <TableCell className="align-top text-end pe-4">
                        <Button size="sm" variant="outline" onClick={() => setReleaseRow(row)}>
                          {failed ? t("Send again") : t("Release")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {totalCount > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
          />
        )}
      </div>

      <ReleaseRefundDialog
        open={releaseRow !== null}
        onOpenChange={(o) => !o && setReleaseRow(null)}
        row={releaseRow}
        onSuccess={() => {
          toast.success(t("Refund released. It is now queued for settlement with the provider."));
          setReleaseRow(null);
          void load();
        }}
      />
    </div>
  );
}
